import React, { useState } from 'react';
import { Trash2, UserCheck, Download, X } from 'lucide-react';
import alertService from '../../utils/alert';

/**
 * Shared bulk-action toolbar for CRM grids. Renders nothing when nothing is
 * selected. Delete always confirms via the same alertService.confirm(...)
 * pattern every page's existing single-record handleDelete already uses.
 *
 * Props:
 *   selectedCount, onClear
 *   statusOptions?: [{value,label}]  onStatusChange?(value)  statusLabel?: string
 *   onAssign?(value)  assignLabel?: string  assignPlaceholder?: string
 *   onDelete()  entityLabel: string (e.g. "lead", "deal") used in copy
 *   onExport?()
 *   children — extra page-specific controls, rendered before Delete
 */
export default function BulkActionBar({
  selectedCount,
  onClear,
  statusOptions,
  onStatusChange,
  statusLabel = 'Status',
  onAssign,
  assignLabel = 'Owner',
  assignPlaceholder = 'Assign to…',
  onDelete,
  onExport,
  entityLabel = 'record',
  children,
}) {
  const [assignValue, setAssignValue] = useState('');

  if (selectedCount === 0) return null;

  const handleDelete = async () => {
    const confirmed = await alertService.confirm(
      `Delete ${selectedCount} ${entityLabel}${selectedCount === 1 ? '' : 's'}?`,
      'This action cannot be undone from this screen.',
      'danger',
      'Delete'
    );
    if (confirmed) onDelete();
  };

  const handleAssign = () => {
    if (!assignValue.trim()) return;
    onAssign(assignValue.trim());
    setAssignValue('');
  };

  return (
    <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 px-4 py-3 bg-[#080707] text-white rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] mb-3">
      <span className="text-[13px] font-bold whitespace-nowrap">
        {selectedCount} selected
      </span>

      {statusOptions && (
        <select
          onChange={(e) => { if (e.target.value) { onStatusChange(e.target.value); e.target.value = ''; } }}
          defaultValue=""
          className="h-8 px-2 bg-white text-[#080707] rounded text-[12px] font-bold outline-none"
        >
          <option value="" disabled>{statusLabel}...</option>
          {statusOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {onAssign && (
        <div className="flex items-center gap-1">
          <input
            value={assignValue}
            onChange={(e) => setAssignValue(e.target.value)}
            placeholder={assignPlaceholder}
            className="h-8 px-2 bg-white text-[#080707] rounded text-[12px] w-36 outline-none"
          />
          <button
            type="button"
            onClick={handleAssign}
            title={assignLabel}
            className="h-8 w-8 flex items-center justify-center bg-[#0176D3] rounded hover:bg-[#014486] transition-colors"
          >
            <UserCheck size={14} />
          </button>
        </div>
      )}

      {onExport && (
        <button
          type="button"
          onClick={onExport}
          className="h-8 px-3 flex items-center gap-1.5 bg-white/10 rounded text-[13px] font-semibold hover:bg-white/20 transition-colors whitespace-nowrap shrink-0"
        >
          <Download size={13} /> Export
        </button>
      )}

      {children}

      <button
        type="button"
        onClick={handleDelete}
        className="h-8 px-3 flex items-center gap-1.5 bg-[#BA0517] rounded text-[13px] font-semibold hover:bg-[#8f0311] transition-colors whitespace-nowrap shrink-0"
      >
        <Trash2 size={13} /> Delete
      </button>

      <button
        type="button"
        onClick={onClear}
        title="Clear selection" aria-label="Clear selection"
        className="ml-auto h-8 w-8 flex items-center justify-center text-white/70 hover:text-white transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
}
